import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewContainerRef, ComponentRef } from '@angular/core';
import { injectComponent } from '@app/core/utils/inject-component';
import { Columns } from '@shared/interfaces/table/column-config';
import { TooltipDirective } from '@shared/directives/tooltip/tooltip.directive';

/**
 * @internal
 * TableCellComponent is used internally by the table component to render individual cells.
 */
@Component({
  selector: 'app-table-cell',
  imports: [
    TooltipDirective,
  ],
  templateUrl: './table-cell.component.html',
  styleUrl: './table-cell.component.css'
})
export class TableCellComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Configuration object for the column, defining how the cell should behave and render.
   */
  @Input() public config?: Columns<any>;

  /**
   * The data object representing the row data for this cell.
   */
  @Input() public data?: { [key: string]: any };

  /**
   * The value extracted from the data according to the config key, used for display.
   */
  public value?: string | number | boolean | void

  private rendererRef?: ComponentRef<any>;

  constructor(
    private readonly viewContainerRef: ViewContainerRef,
  ) { }

  /**
   * Lifecycle hook that initializes the component.
   * It fetches the value from the data based on the config key and dynamically injects a custom cell renderer if specified.
   */
  public ngOnInit(): void {
    this.setValueFromData();
    this.injectRenderer();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // When data changes, update value and pass new inputs into existing renderer if present
    if (changes['data']) {
      this.setValueFromData();
      if (this.rendererRef?.instance) {
        this.rendererRef.setInput('data', this.data);
        this.rendererRef.setInput('params', this.config);
        this.rendererRef.changeDetectorRef.detectChanges();
      }
    }

    // When config changes (e.g., new cellRendererParams.format), reinject renderer so it re-runs ngOnInit
    if (changes['config'] && !changes['config'].firstChange) {
      // If the renderer type changed or params changed, re-inject to force re-init
      this.reinjectRenderer();
    }
  }

  public ngOnDestroy(): void {
    this.destroyRenderer();
  }

  private setValueFromData(): void {
    if (this.config && this.data && typeof this.config.key === "string") {
      this.value = this.fetchData(this.data, this.config.key);
    }
  }

  private injectRenderer(): void {
    if (this.config?.cellRenderer) {
      this.rendererRef = injectComponent(
        this.viewContainerRef,
        this.config.cellRenderer,
        {
          params: this.config,
          data: this.data,
        }
      );
    }
  }

  private reinjectRenderer(): void {
    // Clear view and destroy previous renderer
    this.viewContainerRef.clear();
    this.destroyRenderer();
    // Recreate renderer with updated config/params
    this.injectRenderer();
  }

  private destroyRenderer(): void {
    if (this.rendererRef) {
      this.rendererRef.destroy();
      this.rendererRef = undefined;
    }
  }

  /**
   * Extracts and returns the value from the data object for the given key.
   * Supports string, number, and boolean types.
   * @param data The data object containing row values.
   * @param key The key to fetch the value from the data object.
   * @returns The value corresponding to the key if it is a supported type; otherwise, undefined.
   */
  public fetchData(data: { [key: string]: any }, key: string | number): string | number | boolean | void {
    if (typeof data[key] === "string" || typeof data[key] === "number" || typeof data[key] === "boolean") {
      return data[key];
    }
  }
}
